import { useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, loaders } from 'entities';

import { Table, Row, Button, IconButton, Card } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { InvoiceModal } from 'pages/Invoice/components';

import { CircleXIcon, ReceiptLongIcon } from 'components/Icons';
import { TriStateWrapper } from '../components';

import { Justification } from './Justification';

import { usePendingActions } from 'providers/PendingActions';
import { useJustification } from 'providers/Justification';

import { formatCurrency } from 'utils/text';
import { listRemove } from 'utils/array';

export function InvoiceCancelation() {
  const { t } = useLang();
  const { decreaseCount } = usePendingActions();

  const [invoice, setInvoice] = useState<string | null>(null);

  const { data, loading, overwrite } = loaders.invoice.activeInvoiceCancelationRequests.use({
    defaultValue: [],

    realTimeSubscription: {
      type: 'INVOICE_CANCELATION_REQUEST_UPDATED',

      onMessage: ({ request }, requests) => listRemove(requests, request?.id),
    },
  });

  const { openJustificationModal } = useJustification();

  const rejectInvoice = actions.invoice.rejectInvoiceCancelation.use({
    onSuccess: (_, params) => {
      overwrite((old) => listRemove(old, params.invoice, 'invoice'));
      decreaseCount('UNASSIGNED_INVOICE_CONTROLS');
    },
  });

  const openReject = useCallback(
    (invoiceId: string) => {
      openJustificationModal({
        text: t('why-are-you-rejecting-this-cancelation'),
        onConfirm: async (justification) =>
          rejectInvoice.execute({
            invoice: invoiceId,
            justification,
          }),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openJustificationModal, rejectInvoice.execute, t],
  );

  return (
    <TriStateWrapper loading={loading} message={!data.length && t('no-active-requests-found')}>
      <InvoiceModal invoice={invoice} close={() => setInvoice(null)} />

      <Card>
        <Table
          items={data}
          uniqueKey="id"
          headerClassName="pb-4"
          columns={[
            {
              label: t('client') as string,
              render: ({ invoiceData }) => invoiceData.clientName,
              filter: true,
            },
            {
              label: t('country') as string,
              render: ({ invoiceData }) => invoiceData.country,
              filter: true,
            },
            {
              label: t('value') as string,
              render: ({ invoiceData }) => formatCurrency(invoiceData.value, invoiceData.currency),
              filter: true,
              getTextValue: ({ invoiceData }) => invoiceData.currency,
            },
            {
              label: t('requested-by') as string,
              filter: true,
              getTextValue: ({ requestedBy }) => requestedBy.name,
              render: ({ requestedBy }) => (
                <UserAvatar user={requestedBy} size={32} className="text-sm" name />
              ),
            },
            {
              label: t('justification') as string,

              className: 'w-[540px]',

              render: ({ justification }) => <Justification value={justification} />,
            },
            {
              render: (request) => (
                <Row justifyContent="flex-end">
                  <Button
                    variant="ghost"
                    color="warning"
                    onClick={() => openReject(request.invoice)}
                    loading={rejectInvoice.loading}
                  >
                    <CircleXIcon />
                    {t('reject')}
                  </Button>

                  <IconButton onClick={() => setInvoice(request.invoice)}>
                    <ReceiptLongIcon />
                  </IconButton>
                </Row>
              ),
            },
          ]}
        />
      </Card>
    </TriStateWrapper>
  );
}
